/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { FC } from 'react'
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom'
import { PrivateRoutes } from './PrivateRoutes'
import { AuthPage, useAuth, getAuth } from '../modules/auth'
import { App } from '../App'
import { LinkedInCallback } from 'react-linkedin-login-oauth2'

import { VerifyInvite } from '../modules/auth/components/VerifyInvite'
import { PrivacyPolicy } from '../pages/PrivacyPolicy'
import { TermsOfUse } from '../pages/TermsOfUse'
import { AuthLayout } from '../modules/auth/AuthLayout'
import { ResetPassword } from '../modules/auth/views/ResetPassword'
import { ErrorsPage } from '../modules/errors/ErrorsPage'
import { InvestorUpdatePreview } from '../modules/investor-updates/views/InvestorUpdatePreview'
import { CompanyDetailsPage } from '../modules/company-page/views/CompanyDetailsPage'
import DataRoomPreview from '../modules/data-rooms/view/DataRoomPreview'
import ContentPreview from '../modules/data-rooms/view/ContentPreview'
//import {VerifyRegistration} from '../modules/auth/views/VerifyRegistration'
import FilePreview from '../modules/data-rooms/view/FilePreview'
import { useModuleStatus } from '../hooks/useModuleStatus'
import { MeetingInvite } from '../modules/auth/views/MeetingInvite'
import RecordKeeperPreview from '../modules/recordskeeper-ai/views/RecordKeeperPreview'
import RecordKeeperPublicRequest from '../modules/recordskeeper-ai/views/RecordKeeperPublicRequest'
import PersonalBusinessOTP from '../modules/onboarding/views/PersonalBusinessOTP'
import { OTPRoutes } from '../modules/onboarding/OTPRoutes'
import QueryPromptTable from '../modules/recordskeeper-ai/components/QueryPromptTable'
import UnderMaintainance from '../modules/onboarding/UnderMaintainance'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */

const AppRoutes: FC = () => {
  const { userToken, setUserToken } = useAuth()
  const { moduleStatus } = useModuleStatus()

  const token = getAuth()
  if (token) {
    setUserToken(token.token)
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<App />}>
          <Route path='under-maintainance' element={<UnderMaintainance />} />
          <Route element={<AuthLayout />}>
            <Route path='terms-of-use' element={<TermsOfUse />} />
            <Route path='meeting-invite' element={<MeetingInvite />} />
            <Route path='privacy-policy' element={<PrivacyPolicy />} />
            <Route path='public-request-file/*' element={<RecordKeeperPublicRequest />} />
            <Route path='auth/reset-password/:token' element={<ResetPassword />} />
            <Route path='public-query-details' element={<QueryPromptTable />} />
          </Route>
          {userToken ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route element={<AuthLayout />}>
                <Route path='/auth/info' element={<PersonalBusinessOTP />} />
                <Route path='/auth/verify-phone/*' element={<OTPRoutes />} />
              </Route>

              <Route path='/auth/invite/:inviteToken' element={<VerifyInvite />} />
              {moduleStatus['investor-updates'] !== false && (
                <>
                  <Route path='updates/preview/:id' element={<InvestorUpdatePreview />} />
                  <Route path='updates/view/:id' element={<InvestorUpdatePreview />} />
                </>
              )}

              {moduleStatus['company-page'] !== false && (
                <Route path='company-page/public/:slug' element={<CompanyDetailsPage />} />
              )}

              {moduleStatus['data-rooms'] !== false && (
                <>
                  <Route path='/data-rooms/public/:uuid' element={<DataRoomPreview />} />
                  <Route path='/contents/public/:uuid' element={<ContentPreview />} />
                  <Route
                    path='/file/view/:contentId'
                    element={
                      <>
                        {' '}
                        <FilePreview />
                      </>
                    }
                  />
                </>
              )}
            </>
          ) : (
            <>
              <Route index element={<Navigate to='/auth/login' />} />
              <Route path='auth/linkedin' element={<LinkedInCallback />} />
              <Route path='/auth/invite/:inviteToken' element={<VerifyInvite />} />
              <Route path='auth/*' element={<AuthPage />} />

              {moduleStatus['investor-updates'] !== false && (
                <Route path='updates/view/:id' element={<InvestorUpdatePreview />} />
              )}

              {moduleStatus['company-page'] !== false && (
                <Route path='company-page/public/:slug' element={<CompanyDetailsPage />} />
              )}

              {moduleStatus['data-rooms'] !== false && (
                <>
                  <Route path='/data-rooms/public/:uuid' element={<DataRoomPreview />} />
                  <Route path='/contents/public/:uuid' element={<ContentPreview />} />
                  <Route
                    path='/file/view/:contentId'
                    element={
                      <>
                        {' '}
                        <FilePreview />
                      </>
                    }
                  />
                </>
              )}
              <Route
                path='public/records-classifications/files-details/:id'
                element={<RecordKeeperPreview />}
              />

              <Route path='*' element={<ErrorsPage />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export { AppRoutes }
